import React, { useState, useEffect } from "react";
import withClinic from "../hooks/with_clinic";
import { Clinic } from "../store";
import { Row, Col, Typography, Empty, Button } from "antd";
import { FileExcelFilled } from "@ant-design/icons";
import DateRange from "../components/date_range";
import { Form } from "antd";
import { Table } from "antd";
import { addDays, format, subDays } from "date-fns";
import { useQuery, useApolloClient } from "@apollo/client";
import { getBookingDetailsVar, get_booking_details } from "../graphql/booking";
import LoadingSpinner from "../components/loading_indicator";
import Error from "../components/apollo_error";
import { Select } from "antd";
import { PaginationConfig } from "antd/lib/pagination";
import { ExportExcel } from "../helpers/excel";
import { formatPhoneNumber } from "../helpers/utils";
import HorizontalScrollContainer from "../components/horizontal_scroll_container";

interface BookingDetailsResult {
  bookingid: string;
  FromTime: Date;
  ToTime: Date;
  ServiceName: string;
  MemberName: string;
  MemberPhoneNumber: string;
  PractitionerName: string;
  ClinicName: string;
  ClinicCode: string;
  ClinicID: string;
  HelperName: string | null;
  status: string;
  member_note: string | null;
  merchant_note: string | null;
}

interface BookingDetailsResponse {
  data: BookingDetailsResult[];
  totalCount: number;
}

interface P {
  currentClinic?: Clinic;
}

const Appoinment = (props: P) => {
  const clinicCode = props.currentClinic?.code || "";
  const client = useApolloClient();
  const [filter, setFilter] = useState<string | null>(null);
  const [currentBookings, setCurrentBookings] = useState<BookingDetailsResult[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState<PaginationConfig | null>({
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50"],
  });
  const [ranges, setRanges] = useState({
    startDate: subDays(new Date(), 15),
    endDate: addDays(new Date(), 15),
  });
  const [form] = Form.useForm();

  const take = pagination?.pageSize || 20;
  const skip = ((pagination?.current || 1) - 1) * take;

  const variables = getBookingDetailsVar({
    clinicCode,
    startDate: ranges.startDate,
    endDate: ranges.endDate,
    status: filter,
    skip,
    take,
  });

  const { loading, data, error } = useQuery<{
    getBookingDetails: BookingDetailsResponse;
  }>(get_booking_details, { 
    variables,
    notifyOnNetworkStatusChange: true,
  });

  // Reset bookings when filters change
  useEffect(() => {
    setCurrentBookings([]);
    setTotalCount(0);
    setPagination((prev: PaginationConfig | null) => ({ ...prev, current: 1 }));
  }, [ranges.startDate, ranges.endDate, filter]);

  // Update bookings when new data arrives
  useEffect(() => {
    if (data?.getBookingDetails) {
      setCurrentBookings(data.getBookingDetails.data);
      setTotalCount(data.getBookingDetails.totalCount);
    }
  }, [data]);

  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
  };

  const handleTableChange = (newPagination: PaginationConfig) => {
    setPagination(newPagination);
  };

  const handleExport = async () => {
    const fileName =
      "appointments_" +
      format(ranges.startDate, "yyyy-MMM-dd") +
      "_" +
      format(ranges.endDate, "yyyy-MMM-dd");
    let excelData: any[] = [];
    const header = [
      "From Time",
      "To Time",
      "Service",
      "Member",
      "Phone",
      "Practitioner",
      "Status",
      "Helper",
      "Member Note",
      "Merchant Note",
    ];

    let list: BookingDetailsResult[] = [];
    let exportSkip = 0;
    const exportTake = 500;
    let totalCount = 0;
    setExporting(true);

    try {
      while (true) {
        const variables = getBookingDetailsVar({
          clinicCode,
          startDate: ranges.startDate,
          endDate: ranges.endDate,
          status: filter,
          skip: exportSkip,
          take: exportTake,
        });

        const result = await client.query<{
          getBookingDetails: BookingDetailsResponse;
        }>({
          query: get_booking_details,
          variables,
        });

        const response = result.data?.getBookingDetails;
        if (!response) break;

        const bookings = response.data || [];
        list = list.concat(bookings);
        
        // Use totalCount from first response to track progress
        if (totalCount === 0 && response.totalCount > 0) {
          totalCount = response.totalCount;
        }
        
        // Check if we got a full page and there's more data
        if (bookings.length === exportTake && list.length < totalCount) {
          exportSkip += exportTake;
        } else {
          break;
        }
      }

      for (const booking of list) {
        const fromTime = format(new Date(booking.FromTime), "MMM dd, hh:mm a");
        const toTime = format(new Date(booking.ToTime), "MMM dd, hh:mm a");
        const phone = formatPhoneNumber(booking.MemberPhoneNumber, props.currentClinic);

        excelData.push({
          "From Time": fromTime,
          "To Time": toTime,
          Service: booking.ServiceName,
          Member: booking.MemberName,
          Phone: phone,
          Practitioner: booking.PractitionerName,
          Status: booking.status,
          Helper: booking.HelperName || "",
          "Member Note": booking.member_note || "",
          "Merchant Note": booking.merchant_note || "",
        });
      }

      ExportExcel(excelData, fileName, header);
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setExporting(false);
    }
  };

  if (loading && currentBookings.length === 0) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  const columns: any[] = [
    {
      title: "From Time",
      dataIndex: "FromTime",
      key: "FromTime",
      width: 150,
      sorter: (a: BookingDetailsResult, b: BookingDetailsResult) =>
        new Date(a.FromTime).getTime() - new Date(b.FromTime).getTime(),
      render: (v: Date) => {
        return format(new Date(v), "MMM dd, hh:mm a");
      },
    },
    {
      title: "To Time",
      dataIndex: "ToTime",
      key: "ToTime",
      width: 150,
      sorter: (a: BookingDetailsResult, b: BookingDetailsResult) =>
        new Date(a.ToTime).getTime() - new Date(b.ToTime).getTime(),
      render: (v: Date) => {
        return format(new Date(v), "MMM dd, hh:mm a");
      },
    },
    {
      title: "Service",
      dataIndex: "ServiceName",
      key: "ServiceName",
      sorter: (a: BookingDetailsResult, b: BookingDetailsResult) =>
        a.ServiceName.toLowerCase().localeCompare(b.ServiceName.toLowerCase()),
    },
    {
      title: "Member",
      dataIndex: "MemberName",
      key: "MemberName",
      sorter: (a: BookingDetailsResult, b: BookingDetailsResult) =>
        a.MemberName.toLowerCase().localeCompare(b.MemberName.toLowerCase()),
    },
    {
      title: "Phone",
      dataIndex: "MemberPhoneNumber",
      key: "MemberPhoneNumber",
      render: (phone: string) => formatPhoneNumber(phone, props.currentClinic),
    },
    {
      title: "Practitioner",
      dataIndex: "PractitionerName",
      key: "PractitionerName",
      sorter: (a: BookingDetailsResult, b: BookingDetailsResult) =>
        a.PractitionerName.toLowerCase().localeCompare(
          b.PractitionerName.toLowerCase()
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a: BookingDetailsResult, b: BookingDetailsResult) =>
        a.status.localeCompare(b.status),
    },
    {
      title: "Helper",
      dataIndex: "HelperName",
      key: "HelperName",
      sorter: (a: BookingDetailsResult, b: BookingDetailsResult) =>
        (a.HelperName || "")
          .toLowerCase()
          .localeCompare((b.HelperName || "").toLowerCase()),
    },
    {
      title: "Member Note",
      dataIndex: "member_note",
      key: "member_note",
    },
    {
      title: "Merchant Note",
      dataIndex: "merchant_note",
      key: "merchant_note",
    },
  ];
  const control = (
    <Row style={{ marginTop: -18 }} gutter={8}>
      <Col flex={4}>
        <Row gutter={8}>
          <Col span={8}>
            <Typography level={2}>Appointments</Typography>
          </Col>
          <Col span={8}>
            <Typography.Text type="secondary">
              Total: {totalCount} appointments
            </Typography.Text>
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <Row gutter={8}>
          <Col>
            <DateRange
              startDate={ranges.startDate}
              endDate={ranges.endDate}
              onSelect={onDateChange}
            />
          </Col>
          <Col>
            <Button
              size="middle"
              loading={exporting}
              icon={<FileExcelFilled />}
              onClick={handleExport}
            >
              Export
            </Button>
          </Col>
        </Row>
      </Col>
    </Row>
  );

  const filters = (
    <Row style={{ marginTop: 4 }}>
      <Col span={8}>
        <Select
          style={{ width: "100%" }}
          placeholder="Select appointment status"
          allowClear
          value={filter}
          onChange={(val: string) => setFilter(val || null)}
          options={[
            { label: "REQUEST", value: "REQUEST" },
            { label: "BOOKED", value: "BOOKED" },
            { label: "CHECKIN", value: "CHECKIN" },
            { label: "CHECKOUT", value: "CHECKOUT" },
            { label: "MEMBER_CANCEL", value: "MEMBER_CANCEL" },
            { label: "MERCHANT_CANCEL", value: "MERCHANT_CANCEL" },
            { label: "NO_SHOW", value: "NO_SHOW" },
          ]}
        />
      </Col>
    </Row>
  );
  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form form={form} component={false}>
          <HorizontalScrollContainer>
            <Table
              showHeader={currentBookings.length > 0}
              dataSource={currentBookings.map((b) => ({ key: b.bookingid, ...b }))}
              columns={columns}
              rowClassName="editable-row"
              loading={loading}
              scroll={{ x: 1200 }}
              pagination={{
                ...pagination,
                total: totalCount,
                showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} items`,
              }}
              onChange={handleTableChange}
            />
          </HorizontalScrollContainer>
        </Form>
      </Col>
    </Row>
  );

  return (
    <React.Fragment>
      {control}
      {filters}
      {context}
    </React.Fragment>
  );
};

export default withClinic(Appoinment);
